import { useState, useEffect } from "react"
import { Trash2, AlertTriangle, X, Loader2 } from "lucide-react"

export default function DeleteAccountModal({ open, onClose, onConfirm, busy = false }) {
  const [acknowledged, setAcknowledged] = useState(false)

  useEffect(() => {
    if (!open) setAcknowledged(false)
  }, [open])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="delete-account-title">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={busy ? undefined : onClose} aria-hidden="true" />

      {/* Modal */}
      <div className="relative w-full max-w-md overflow-hidden rounded-xl border border-white/10 bg-ink-800 p-4 shadow-2xl">
        {/* Gold top accent */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-[2px]"
          style={{ background: "linear-gradient(90deg, transparent, #D9B36C 50%, transparent)" }}
          aria-hidden="true"
        />

        <div className="flex items-start gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-red-500/30 bg-red-500/10 text-red-400">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h2 id="delete-account-title" className="font-display text-base font-bold text-paper-0">
              Are you sure?
            </h2>
            <p className="mt-1 text-sm text-mist-300">
              This will permanently delete your account{" "}
              <span className="font-semibold text-red-400">and cannot be restored.</span>
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="flex h-8 w-8 items-center justify-center rounded text-mist-400 transition-colors hover:text-paper-0 disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Acknowledge tick */}
        <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg border border-white/10 bg-ink-900/60 px-3 py-2.5 transition-colors hover:border-white/20">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            disabled={busy}
            className="mt-0.5 h-4 w-4 shrink-0 accent-red-500"
          />
          <span className="text-sm text-mist-300">
            I understand that deleting my account{" "}
            <span className="font-semibold text-paper-0">cannot be undone</span> and I won't be able to restore it.
          </span>
        </label>

        <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            onClick={onClose}
            disabled={busy}
            className="flex h-8 items-center justify-center rounded-lg border border-white/15 px-3 text-sm font-semibold text-paper-0 transition-colors hover:bg-white/5 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!acknowledged || busy}
            className="flex h-8 items-center justify-center gap-2 rounded-lg bg-red-500 px-3 text-sm font-semibold text-white transition-colors hover:bg-red-400 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Delete Account
          </button>
        </div>
      </div>
    </div>
  )
}